// player/playback.ts — the player's small pure rules, kept out of the component so they are
// testable without a media element: A–B repeat, the sleep timer and keyboard shortcuts.

export type RepeatMode = 'off' | 'loop' | 'ab';

export interface AbRange {
  a: number | null;
  b: number | null;
}

/** Where playback should jump for an A–B repeat at time `t`, or null to carry on. */
export function abJump(range: AbRange, t: number): number | null {
  if (range.a === null || range.b === null || range.b <= range.a) return null;
  return t >= range.b || t < range.a - 0.25 ? range.a : null;
}

/** Sleep timer choices; 'end' pauses when the current video finishes. */
export type SleepChoice = 'off' | 5 | 10 | 15 | 30 | 45 | 60 | 'end';
export const SLEEP_CHOICES: SleepChoice[] = ['off', 5, 10, 15, 30, 45, 60, 'end'];

export function sleepLabel(choice: SleepChoice): string {
  if (choice === 'off') return 'Off';
  if (choice === 'end') return 'End of video';
  return `${choice} minutes`;
}

export interface Shortcut {
  keys: string;
  action: string;
}

/** Shown in the Keyboard shortcuts dialog — the same table `shortcutFor` implements. */
export const SHORTCUTS: Shortcut[] = [
  { keys: 'Space or K', action: 'Play / pause' },
  { keys: 'J / L', action: 'Back / forward 10 seconds' },
  { keys: '← / →', action: 'Back / forward 5 seconds' },
  { keys: '↑ / ↓', action: 'Volume up / down' },
  { keys: 'M', action: 'Mute' },
  { keys: 'C', action: 'Captions on / off' },
  { keys: 'F', action: 'Fullscreen' },
  { keys: 'T', action: 'Theater mode' },
  { keys: 'I', action: 'Picture in picture' },
  { keys: '0 – 9', action: 'Jump to 0% – 90%' },
  { keys: '< / >', action: 'Slower / faster' },
  { keys: 'Home / End', action: 'Start / end' },
  { keys: '?', action: 'Show these shortcuts' },
];

export type ShortcutAction =
  | { type: 'toggle' }
  | { type: 'seekBy'; seconds: number }
  | { type: 'seekFraction'; fraction: number }
  | { type: 'seekTo'; where: 'start' | 'end' }
  | { type: 'volumeBy'; delta: number }
  | { type: 'mute' }
  | { type: 'fullscreen' }
  | { type: 'theater' }
  | { type: 'pip' }
  | { type: 'speedBy'; delta: number }
  | { type: 'captions' }
  | { type: 'help' };

/** Map a keydown to a player action. Modifier chords are left to the browser. */
export function shortcutFor(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>) {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const k = e.key;
  switch (k) {
    case ' ':
    case 'k':
    case 'K':
      return { type: 'toggle' } as ShortcutAction;
    case 'j':
    case 'J':
      return { type: 'seekBy', seconds: -10 } as ShortcutAction;
    case 'l':
    case 'L':
      return { type: 'seekBy', seconds: 10 } as ShortcutAction;
    case 'ArrowLeft':
      return { type: 'seekBy', seconds: -5 } as ShortcutAction;
    case 'ArrowRight':
      return { type: 'seekBy', seconds: 5 } as ShortcutAction;
    case 'ArrowUp':
      return { type: 'volumeBy', delta: 0.05 } as ShortcutAction;
    case 'ArrowDown':
      return { type: 'volumeBy', delta: -0.05 } as ShortcutAction;
    case 'm':
    case 'M':
      return { type: 'mute' } as ShortcutAction;
    case 'f':
    case 'F':
      return { type: 'fullscreen' } as ShortcutAction;
    case 'c':
    case 'C':
      return { type: 'captions' } as ShortcutAction;
    case 't':
    case 'T':
      return { type: 'theater' } as ShortcutAction;
    case 'i':
    case 'I':
      return { type: 'pip' } as ShortcutAction;
    case '<':
    case ',':
      return { type: 'speedBy', delta: -0.25 } as ShortcutAction;
    case '>':
    case '.':
      return { type: 'speedBy', delta: 0.25 } as ShortcutAction;
    case 'Home':
      return { type: 'seekTo', where: 'start' } as ShortcutAction;
    case 'End':
      return { type: 'seekTo', where: 'end' } as ShortcutAction;
    case '?':
      return { type: 'help' } as ShortcutAction;
    default:
      if (/^[0-9]$/.test(k))
        return { type: 'seekFraction', fraction: Number(k) / 10 } as ShortcutAction;
      return null;
  }
}

/** True when a keystroke belongs to a text field, not the player. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type;
    return !['checkbox', 'radio', 'range', 'button', 'submit'].includes(type);
  }
  return false;
}
